import axios from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { api } from '../../configurations/api';
import { userMediaAssetByIdUrl, userMediaAssetsUrl, userMediaPresignUrl } from '../../configurations/api/url';
import { httpRequest } from '../types';
import { SentryCapture } from '../../analytics/Sentry';
import { globalErrorHandler } from '../error/saga';
import { getFileExtension, showToastMessage } from '../../utils/AppUtils';
import { compressImageForUpload } from '../../utils/compressImageForUpload';
import {
    normalizeRegisteredUserMediaAsset,
    readImageDimensions,
    unwrapUserMediaData,
} from '../../utils/uploadUserMediaAsset';
import type {
    IRegisterUserMediaAssetPayload,
    IUserMediaAsset,
    IUserMediaPagination,
    IUserMediaPresignData,
} from '../../types/global/userMedia.types';
import {
    deleteUserMediaAsset,
    deleteUserMediaAssetFail,
    deleteUserMediaAssetSuccess,
    listUserMediaAssets,
    listUserMediaAssetsFail,
    listUserMediaAssetsSuccess,
    prependUserMediaAsset,
    updateUserMediaAsset,
    updateUserMediaAssetFail,
    updateUserMediaAssetSuccess,
    uploadUserMediaLibraryImage,
    uploadUserMediaLibraryImageFail,
    uploadUserMediaLibraryImageSuccess,
    type IListUserMediaAssetsPayload,
    type IUpdateUserMediaAssetPayload,
    type IUploadUserMediaLibraryImagePayload,
} from './actions';
import { isUserMediaListCacheFresh } from './reducer';
import { userMediaListCacheAtSelector } from './selectors';
import type { RootState } from '../root-reducer';

const EMAIL_IMAGE_MAX_BYTES = 450 * 1024;

export function buildUserMediaListCacheKey(page: number, limit: number, q: string): string {
    return `p=${page}|l=${limit}|q=${q}`;
}

export function normalizeUserMediaAsset(raw: Record<string, unknown>): IUserMediaAsset | null {
    const id = String(raw.id ?? raw._id ?? '');
    const url = String(raw.url ?? raw.public_url ?? '');
    if (!url) return null;
    return {
        id: id || url,
        user_id: raw.user_id != null ? String(raw.user_id) : undefined,
        name: String(raw.name ?? raw.filename ?? url.split('/').pop() ?? 'image'),
        url,
        file_key: String(raw.file_key ?? raw.key ?? ''),
        mime_type: (raw.mime_type as string | null) ?? null,
        size_bytes: raw.size_bytes != null ? Number(raw.size_bytes) : null,
        width: raw.width != null ? Number(raw.width) : null,
        height: raw.height != null ? Number(raw.height) : null,
        category: String(raw.category ?? 'email'),
        created_at: raw.created_at != null ? String(raw.created_at) : undefined,
        updated_at: raw.updated_at != null ? String(raw.updated_at) : undefined,
        tags: Array.isArray(raw.tags) ? (raw.tags as string[]) : undefined,
        description: raw.description != null ? String(raw.description) : null,
    };
}

function parseListResponse(
    response: { data?: Record<string, unknown> },
    fallbackPage: number,
    fallbackLimit: number,
): { assets: IUserMediaAsset[]; pagination: IUserMediaPagination } {
    const root = (response.data?.data ?? response.data) as Record<string, unknown> | undefined;
    const rawList = (root?.assets ?? root?.items ?? root?.data ?? root?.images) as unknown;
    const list = Array.isArray(rawList) ? rawList : [];
    const assets = list
        .map(item =>
            item && typeof item === 'object' ? normalizeUserMediaAsset(item as Record<string, unknown>) : null,
        )
        .filter((a): a is IUserMediaAsset => Boolean(a));

    const nested = (root?.pagination ?? {}) as Record<string, number>;
    const page = Number(nested.page ?? root?.page ?? fallbackPage) || fallbackPage;
    const limit = Number(nested.limit ?? root?.limit ?? fallbackLimit) || fallbackLimit;
    const total = Number(nested.total ?? root?.total ?? assets.length) || 0;
    let total_pages = Number(nested.total_pages ?? root?.total_pages) || 0;
    if (!total_pages) {
        total_pages = limit > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;
    }
    if (assets.length < limit) {
        total_pages = Math.min(total_pages, page);
    }

    return { assets, pagination: { page, limit, total, total_pages } };
}

function* listUserMediaAssetsSaga({ payload }: { payload: IListUserMediaAssetsPayload }): Generator<any, any, any> {
    const page = Math.max(1, payload?.page ?? 1);
    const limit = Math.min(50, Math.max(1, payload?.limit ?? 12));
    const q = (payload?.q ?? '').trim();
    const append = Boolean(payload?.append);
    const cacheKey = buildUserMediaListCacheKey(page, limit, q);

    try {
        if (!payload?.force && !append) {
            const listCacheAt: Record<string, number> = yield select(userMediaListCacheAtSelector);
            const assets: IUserMediaAsset[] = yield select((s: RootState) => s.userMedia?.assets ?? []);
            if (isUserMediaListCacheFresh(listCacheAt, cacheKey) && assets.length > 0) {
                yield put(
                    listUserMediaAssetsSuccess({
                        assets,
                        pagination: (yield select((s: RootState) => s.userMedia?.pagination)) ?? {
                            page,
                            limit,
                            total: assets.length,
                            total_pages: 1,
                        },
                        append: false,
                        cacheKey,
                        q,
                    }),
                );
                return;
            }
        }

        const qs = new URLSearchParams({ page: String(page), limit: String(limit) });
        if (q) qs.set('q', q);
        const response = yield call(api, `${userMediaAssetsUrl}?${qs}`, httpRequest.GET, null);
        const { assets, pagination } = parseListResponse(response, page, limit);
        yield put(
            listUserMediaAssetsSuccess({
                assets,
                pagination: { ...pagination, page },
                append,
                cacheKey,
                q,
            }),
        );
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(listUserMediaAssetsFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* updateUserMediaAssetSaga({ payload }: { payload: IUpdateUserMediaAssetPayload }): Generator<any, any, any> {
    const { id, meta } = payload;
    try {
        const body = {
            name: meta.name.trim(),
            tags: meta.tags,
            description: meta.description?.trim() || null,
        };
        const response = yield call(api, userMediaAssetByIdUrl(id), httpRequest.PATCH, body);
        const raw = (response.data?.data ?? response.data) as Record<string, unknown>;
        const existing: IUserMediaAsset | undefined = yield select((s: RootState) =>
            (s.userMedia?.assets ?? []).find(a => a.id === id),
        );
        const normalized = normalizeUserMediaAsset(raw && typeof raw === 'object' ? raw : {}) ?? {
            ...(existing as IUserMediaAsset),
            id,
            name: body.name,
            tags: body.tags,
            description: body.description,
        };
        yield put(updateUserMediaAssetSuccess(normalized));
        yield call(showToastMessage, 'Image details updated', 'success');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(updateUserMediaAssetFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* deleteUserMediaAssetSaga({ payload }: { payload: string }): Generator<any, any, any> {
    try {
        yield call(api, userMediaAssetByIdUrl(payload), httpRequest.DELETE, null);
        yield put(deleteUserMediaAssetSuccess(payload));
        yield call(showToastMessage, 'Image removed from library', 'info');
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(deleteUserMediaAssetFail(error));
        yield call(globalErrorHandler, error);
    }
}

function* uploadUserMediaLibraryImageSaga({
    payload,
    resolve,
    reject,
}: {
    payload: IUploadUserMediaLibraryImagePayload;
    resolve?: (v: any) => void;
    reject?: (e: any) => void;
}): Generator<any, any, any> {
    try {
        const file = payload.file;
        const category = payload.category ?? 'email';
        const compressed: File = yield call(compressImageForUpload, file, {
            targetBytes: EMAIL_IMAGE_MAX_BYTES,
            maxWidth: 1600,
            maxHeight: 1600,
        });

        const extension = getFileExtension(compressed) || 'jpg';
        const contentType = compressed.type || `image/${extension === 'jpg' ? 'jpeg' : extension}`;
        const qs = new URLSearchParams({ extension, content_type: contentType });
        const presignRes = yield call(api, `${userMediaPresignUrl}?${qs}`, httpRequest.GET, null);
        const presign = unwrapUserMediaData<IUserMediaPresignData>(presignRes);

        if (!presign?.upload_url || !presign?.file_key || !presign?.public_url) {
            throw new Error('Presign URL not returned');
        }

        const putHeaders: Record<string, string> = {
            ...(presign.headers ?? {}),
            'Content-Type':
                (presign.headers && (presign.headers['Content-Type'] || presign.headers['content-type'])) ||
                contentType,
        };
        yield call([axios, axios.put], presign.upload_url, compressed, {
            headers: putHeaders,
            maxContentLength: Infinity,
            withCredentials: false,
        });

        const dims: { width: number | null; height: number | null } = yield call(readImageDimensions, compressed);
        const tags = (payload.tags ?? []).map(t => t.trim()).filter(Boolean);
        const description = payload.description?.trim() || undefined;
        const body: IRegisterUserMediaAssetPayload = {
            name: (payload.name || file.name || compressed.name).trim() || file.name,
            url: presign.public_url,
            file_key: presign.file_key,
            mime_type: contentType,
            size_bytes: compressed.size,
            width: dims.width,
            height: dims.height,
            category,
            ...(tags.length ? { tags } : {}),
            ...(description ? { description } : {}),
        };

        const registerRes = yield call(api, userMediaAssetsUrl, httpRequest.POST, body);
        const raw = unwrapUserMediaData<Record<string, unknown>>(registerRes);
        const asset = normalizeRegisteredUserMediaAsset(raw && typeof raw === 'object' ? raw : {}, body);
        const result = { url: asset.url || presign.public_url, asset };
        yield put(uploadUserMediaLibraryImageSuccess(result));
        yield put(prependUserMediaAsset(asset));
        if (typeof resolve === 'function') resolve(result);
    } catch (error: any) {
        SentryCapture(error, 'error');
        yield put(uploadUserMediaLibraryImageFail(error));
        if (typeof reject === 'function') reject(error);
        else yield call(globalErrorHandler, error);
    }
}

function* listUserMediaAssetsWatcher() {
    yield takeLatest(listUserMediaAssets, listUserMediaAssetsSaga);
}
function* updateUserMediaAssetWatcher() {
    yield takeLatest(updateUserMediaAsset, updateUserMediaAssetSaga);
}
function* deleteUserMediaAssetWatcher() {
    yield takeLatest(deleteUserMediaAsset, deleteUserMediaAssetSaga);
}
function* uploadUserMediaLibraryImageWatcher() {
    yield takeLatest(uploadUserMediaLibraryImage as any, uploadUserMediaLibraryImageSaga);
}

export default function* userMediaSaga() {
    yield all([
        listUserMediaAssetsWatcher(),
        updateUserMediaAssetWatcher(),
        deleteUserMediaAssetWatcher(),
        uploadUserMediaLibraryImageWatcher(),
    ]);
}
